import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import { Badge } from '@/components/ui/badge'
import { Label } from '@/components/ui/label'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { toast } from 'sonner'
import { MessageSquare, ShieldAlert, X, Plus, Save, Instagram, Facebook, MessageCircle } from 'lucide-react'
import { useSaveAutomation } from '@/api/hooks/useAccounts'
import { useLanguage } from '@/i18n/LanguageContext'
import type { Account } from '@/types'

interface Props {
  account: Account
  onSaved?: () => void
}

interface AutoForm {
  autoCommentEnabled: boolean
  autoCommentText: string
  moderationEnabled: boolean
  blockedWords: string[]
  privateReplyEnabled: boolean
  privateReplyMessage: string
}

function AutomationPanel({
  account,
  platform,
  onSaved,
}: {
  account: Account
  platform: 'ig' | 'fb'
  onSaved?: () => void
}) {
  const { t, language } = useLanguage()
  const saveAutomation = useSaveAutomation()
  const isIG = platform === 'ig'
  const [newWord, setNewWord] = useState('')

  const { register, handleSubmit, setValue, watch } = useForm<AutoForm>({
    defaultValues: {
      autoCommentEnabled: isIG ? account.autoCommentEnabled : account.fbAutoCommentEnabled,
      autoCommentText: isIG ? (account.autoCommentText || '') : (account.fbAutoCommentText || ''),
      moderationEnabled: isIG ? account.moderationEnabled : account.fbModerationEnabled,
      blockedWords: isIG ? (account.blockedWords || []) : (account.fbBlockedWords || []),
      privateReplyEnabled: account.privateReplyEnabled || false,
      privateReplyMessage: account.privateReplyMessage || '',
    },
  })

  const autoCommentEnabled = watch('autoCommentEnabled')
  const moderationEnabled = watch('moderationEnabled')
  const blockedWords = watch('blockedWords')
  const privateReplyEnabled = watch('privateReplyEnabled')

  const addWord = () => {
    const word = newWord.trim().toLowerCase()
    if (word && !blockedWords.includes(word)) {
      setValue('blockedWords', [...blockedWords, word])
      setNewWord('')
    }
  }

  const removeWord = (word: string) => {
    setValue('blockedWords', blockedWords.filter((w) => w !== word))
  }

  const onSubmit = (data: AutoForm) => {
    saveAutomation.mutate(
      {
        igUserId: account.igUserId,
        platform,
        ...data,
        ...(isIG ? {} : { privateReplyEnabled: data.privateReplyEnabled, privateReplyMessage: data.privateReplyMessage }),
      },
      {
        onSuccess: () => { toast.success(isIG ? t('Automacao Instagram salva!') : t('Automacao Facebook salva!')); onSaved?.() },
        onError: () => toast.error(t('Erro ao salvar')),
      },
    )
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
      {language === 'en' && (
        <div className="flex items-start gap-2 rounded-lg border border-blue-500/20 bg-blue-500/5 px-4 py-2.5">
          <span className="mt-0.5 text-blue-500">&#8505;</span>
          <p className="text-xs text-muted-foreground">Auto-replies are triggered by user-initiated messages. The app responds within the 24-hour standard messaging window per Meta Platform Policy.</p>
        </div>
      )}
      <div className="grid gap-4 sm:grid-cols-2">
        {/* Auto-comment card */}
        <div className="rounded-xl border border-border bg-card p-4">
          <div className="mb-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`rounded-lg p-2 ${autoCommentEnabled ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'}`}>
                <MessageSquare className="h-4 w-4" />
              </div>
              <div>
                <div className="text-sm font-semibold">{t('Auto-Comentario')}</div>
                <div className="text-[10px] text-muted-foreground">
                  {t('Responde automaticamente a comentarios')}
                </div>
              </div>
            </div>
            <Switch
              checked={autoCommentEnabled}
              onCheckedChange={(val) => setValue('autoCommentEnabled', val)}
            />
          </div>

          {autoCommentEnabled && (
            <div>
              <Label className="mb-1.5 text-[10px] text-muted-foreground">{t('Texto da resposta')}</Label>
              <Textarea
                placeholder={t('Obrigado pelo comentario! Confira nosso inbox...')}
                className="min-h-[100px] resize-none text-sm"
                {...register('autoCommentText')}
              />
            </div>
          )}

          {!autoCommentEnabled && (
            <div className="flex items-center justify-center rounded-lg border border-dashed border-border py-8 text-xs text-muted-foreground/50">
              {t('Ative para configurar')}
            </div>
          )}
        </div>

        {/* Moderation card */}
        <div className="rounded-xl border border-border bg-card p-4">
          <div className="mb-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`rounded-lg p-2 ${moderationEnabled ? 'bg-destructive/10 text-destructive' : 'bg-muted text-muted-foreground'}`}>
                <ShieldAlert className="h-4 w-4" />
              </div>
              <div>
                <div className="text-sm font-semibold">{t('Moderacao')}</div>
                <div className="text-[10px] text-muted-foreground">
                  {t('Oculta comentarios com palavras bloqueadas')}
                </div>
              </div>
            </div>
            <Switch
              checked={moderationEnabled}
              onCheckedChange={(val) => setValue('moderationEnabled', val)}
            />
          </div>

          {moderationEnabled ? (
            <div>
              <Label className="mb-1.5 text-[10px] text-muted-foreground">{t('Palavras bloqueadas')}</Label>
              <div className="mb-2 flex gap-2">
                <Input
                  value={newWord}
                  onChange={(e) => setNewWord(e.target.value)}
                  placeholder={t('Adicionar palavra...')}
                  className="h-8 text-xs"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      addWord()
                    }
                  }}
                />
                <Button type="button" size="sm" variant="outline" className="h-8 px-2" onClick={addWord}>
                  <Plus className="h-3 w-3" />
                </Button>
              </div>

              <div className="flex flex-wrap gap-1.5">
                {blockedWords.map((word) => (
                  <Badge
                    key={word}
                    variant="secondary"
                    className="gap-1 pr-1 text-[10px]"
                  >
                    {word}
                    <button
                      type="button"
                      onClick={() => removeWord(word)}
                      className="ml-0.5 rounded-full p-0.5 hover:bg-destructive/20 hover:text-destructive"
                    >
                      <X className="h-2.5 w-2.5" />
                    </button>
                  </Badge>
                ))}
                {blockedWords.length === 0 && (
                  <span className="text-[10px] text-muted-foreground/50">
                    {t('Nenhuma palavra adicionada')}
                  </span>
                )}
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center rounded-lg border border-dashed border-border py-8 text-xs text-muted-foreground/50">
              {t('Ative para configurar')}
            </div>
          )}
        </div>
      </div>

      {/* Private Reply card - FB only */}
      {!isIG && (
        <div className="rounded-xl border border-border bg-card p-4">
          <div className="mb-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`rounded-lg p-2 ${privateReplyEnabled ? 'bg-blue-500/10 text-blue-600 dark:text-blue-400' : 'bg-muted text-muted-foreground'}`}>
                <MessageCircle className="h-4 w-4" />
              </div>
              <div>
                <div className="text-sm font-semibold">{t('Resposta Privada')}</div>
                <div className="text-[10px] text-muted-foreground">
                  {t('Envia DM automatica via Messenger quando alguem comenta')}
                </div>
              </div>
            </div>
            <Switch
              checked={privateReplyEnabled}
              onCheckedChange={(val) => setValue('privateReplyEnabled', val)}
            />
          </div>

          {privateReplyEnabled ? (
            <div>
              <Label className="mb-1.5 text-[10px] text-muted-foreground">{t('Mensagem da resposta privada')}</Label>
              <Textarea
                placeholder={t('Oi {{nome}}! Vi que voce comentou no nosso post. Confira essa novidade...')}
                className="min-h-[100px] resize-none text-sm"
                {...register('privateReplyMessage')}
              />
              <div className="mt-2 rounded-lg bg-blue-500/5 border border-blue-500/20 px-3 py-2">
                <p className="text-[10px] text-muted-foreground">
                  <strong className="text-blue-600 dark:text-blue-400">{t('Janela de 7 dias')}</strong> — {t('A Private Reply API permite enviar DM em resposta a comentarios com janela de 7 dias (sem limite de 24h). Aparece no Messenger com banner "respondendo ao seu comentario".')}
                </p>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center rounded-lg border border-dashed border-border py-8 text-xs text-muted-foreground/50">
              {t('Ative para configurar')}
            </div>
          )}
        </div>
      )}

      <Button type="submit" disabled={saveAutomation.isPending} className="w-full gap-2">
        <Save className="h-4 w-4" />
        {saveAutomation.isPending ? t('Salvando...') : t('Salvar Automacao')}
      </Button>
    </form>
  )
}

export function Automation({ account, onSaved }: Props) {
  const hasIG = !account.fbOnly

  return (
    <Tabs defaultValue={hasIG ? 'ig' : 'fb'}>
      <TabsList className="mb-4 h-auto gap-1 bg-muted/50 p-1">
        {hasIG && (
          <TabsTrigger value="ig" className="gap-1.5 text-xs data-[state=active]:bg-card">
            <Instagram className="h-3.5 w-3.5" /> Instagram
          </TabsTrigger>
        )}
        <TabsTrigger value="fb" className="gap-1.5 text-xs data-[state=active]:bg-card">
          <Facebook className="h-3.5 w-3.5" /> Facebook
        </TabsTrigger>
      </TabsList>

      {hasIG && (
        <TabsContent value="ig" className="m-0">
          <AutomationPanel account={account} platform="ig" onSaved={onSaved} />
        </TabsContent>
      )}
      <TabsContent value="fb" className="m-0">
        <AutomationPanel account={account} platform="fb" onSaved={onSaved} />
      </TabsContent>
    </Tabs>
  )
}
